import React, { useState, useEffect, useRef } from 'react';
import { ClipboardList, Users, Package, ShieldCheck, FileCheck, Truck } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

const STEPS = [
  {
    n: 1,
    icon: ClipboardList,
    title: 'Sourcing brief',
    body: 'We start from your market, your price points and your shelf. You tell us the categories and positioning; we come back with matched producers.',
  },
  {
    n: 2,
    icon: Users,
    title: 'Producer selection and audit',
    body: 'We visit and vet each producer in person. We check certifications, production capacity, export experience and consistency of supply.',
  },
  {
    n: 3,
    icon: Package,
    title: 'Samples and approval',
    body: 'We ship samples for your evaluation and handle any recipe, format or packaging adjustments with the producer directly.',
  },
  {
    n: 4,
    icon: ShieldCheck,
    title: 'Quality control before loading',
    body: 'Every order is checked before it leaves Italy, not after it reaches you. Lot traceability and analysis certificates accompany each batch.',
  },
  {
    n: 5,
    icon: FileCheck,
    title: 'Documentation and compliance',
    body: 'Health certificates, certificates of origin, customs codes, nutritional and labelling data prepared for your destination market.',
  },
  {
    n: 6,
    icon: Truck,
    title: 'Consolidation and shipping',
    body: 'We combine products from multiple producers into a single shipment, so you order a full container without committing to one supplier.',
  },
];

const ProcessSection = () => {
  const [processAnimated, setProcessAnimated] = useState(false);
  const processRef = useRef(null);

  useEffect(() => {
    const processObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !processAnimated) {
          setProcessAnimated(true);
        }
      },
      { threshold: 0.2 }
    );

    if (processRef.current) {
      processObserver.observe(processRef.current);
    }

    return () => {
      if (processRef.current) {
        processObserver.unobserve(processRef.current);
      }
    };
  }, [processAnimated]);

  return (
    <section id="process" className="py-20 px-6 bg-white" ref={processRef} data-testid="process-section">
      <div className={`container mx-auto max-w-7xl ${processAnimated ? 'process-animate-active' : 'process-animate-ready'}`}>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-blue-900 font-semibold tracking-wide uppercase text-sm process-label">HOW WE WORK</p>
          <h2 className="md:text-5xl !font-bold !text-4xl mt-3 mb-6 text-stone-900">
            <span className="process-headline">From producer to your shelf</span>
          </h2>
          <p className="text-lg text-stone-600 process-subtext">Six steps from your first brief to a container leaving Italy.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <Card
                key={s.n}
                className="border-stone-200 hover:border-blue-900 transition-all duration-300 hover:shadow-xl group"
                data-testid={`process-step-${s.n}`}
              >
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 flex flex-col items-center gap-3">
                      <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-900 transition-colors duration-300">
                        <Icon className="text-blue-900 group-hover:text-white transition-colors duration-300" size={28} strokeWidth={2} />
                      </div>
                      <span className="text-3xl font-bold text-blue-900 leading-none">{s.n}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-stone-900 mb-3">{s.title}</h3>
                      <p className="text-stone-600 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
